<template>
  <div v-if="isMobile && menu.length > 0">
    <div v-if="menu.length <= 1" class="flex items-center gap-2 text-n-0">
      <l-icon size="medium" :name="activeOption?.icon" />
      {{ activeOption?.label }}
    </div>
    <l-dropdown
      v-else
      v-model:open="dropdownOpen"
      :options="menu"
      :mark-selected="false"
      placement="bottom"
      @update:model-value="onSelect"
    >
      <div
        class="flex items-center gap-2 cursor-pointer text-n-0"
        @click="dropdownOpen = !dropdownOpen"
      >
        <l-icon size="medium" :name="activeOption?.icon" />
        {{ activeOption?.label }}
        <l-icon size="small" name="lucide/chevron-down" />
      </div>
    </l-dropdown>
  </div>
  <div v-else-if="menu.length > 0" class="flex gap-2">
    <router-link v-if="floorplanIds.length > 0 || config.fastMode" :to="{ name: 'restaurant' }">
      <div
        class="px-4 py-2 rounded-xl transition-colors cursor-pointer flex items-center gap-2 h-11"
        :class="{
          'bg-v-300': isRouteActive('restaurant')
        }"
      >
        <l-icon size="medium" name="lucide/layout-grid" />
        {{ t('top-menu.restaurant') }}
      </div>
    </router-link>
    <router-link v-if="products.delivery" :to="{ name: 'toGo' }">
      <div
        class="px-4 py-2 rounded-xl transition-colors cursor-pointer flex items-center gap-2 h-11"
        :class="{
          'bg-v-300': isRouteActive('toGo')
        }"
      >
        <l-icon size="medium" name="lucide/shopping-bag" />
        {{ t('top-menu.to-go') }}
      </div>
    </router-link>
    <router-link v-if="products.bookings" :to="{ name: 'reservations' }">
      <div
        class="px-4 py-2 rounded-xl transition-colors cursor-pointer flex items-center gap-2 h-11"
        :class="{
          'bg-v-300': isRouteActive('reservations')
        }"
      >
        <l-icon size="medium" name="lucide/calendar-check" />
        {{ t('top-menu.reservations') }}
      </div>
    </router-link>
  </div>
</template>

<script setup lang="ts">
import { useBreakpoints } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { LDropdown, LIcon } from '@last/core-ui/paprika'
import type { LIconName } from '@last/core-ui/paprika/components/icon.type'

import { useConfigStore } from '@/store/config'
import { useFloorplanStore } from '@/store/floorplan'

const isMobile = useBreakpoints({ compact: 920 }).smaller('compact')

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const dropdownOpen = ref(false)
const { floorplanIds } = storeToRefs(useFloorplanStore())
const { products, config } = storeToRefs(useConfigStore())

type MenuEntry = {
  label: string
  value: string
  icon: LIconName
}

const menu = computed<MenuEntry[]>(() => {
  const entries: MenuEntry[] = []
  if (floorplanIds.value.length > 0) {
    entries.push({
      label: t('top-menu.restaurant'),
      value: 'restaurant',
      icon: 'lucide/layout-grid'
    })
  }
  if (products.value.delivery) {
    entries.push({
      label: t('top-menu.to-go'),
      value: 'toGo',
      icon: 'lucide/shopping-bag'
    })
  }
  if (products.value.bookings) {
    entries.push({
      label: t('top-menu.reservations'),
      value: 'reservations',
      icon: 'lucide/calendar-check'
    })
  }
  return entries
})

const activeOption = computed(
  () => menu.value.find(entry => isRouteActive(entry.value)) ?? menu.value[0]
)

function isRouteActive(parentName: string) {
  return route.matched.some(r => r.name === parentName)
}

function onSelect(values: (string | number)[]) {
  const selected = values[0]
  if (selected) {
    dropdownOpen.value = false
    router.push({ name: String(selected) })
  }
}
</script>
